import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import {
    Mail, Phone, Building2, Clock, RefreshCw, MessageCircle, Send, X, Check,
    Zap, ShoppingCart, HelpCircle, Sparkles,
} from "lucide-react";
import { formatDateTime } from "@/lib/format";

const STATUS_STYLES = {
    new: "bg-orange-50 text-[#F26322] border-orange-200",
    contacted: "bg-sky-50 text-sky-700 border-sky-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    won: "bg-emerald-50 text-emerald-700 border-emerald-200",
    lost: "bg-slate-100 text-slate-500 border-slate-200",
    spam: "bg-red-50 text-red-700 border-red-200",
};

const KIND_META = {
    upgrade: { label: "Upgrade", icon: Zap, color: "text-emerald-700" },
    enterprise: { label: "Enterprise", icon: Sparkles, color: "text-purple-700" },
    additional_modules: { label: "Modules", icon: ShoppingCart, color: "text-blue-700" },
    demo: { label: "Demo", icon: HelpCircle, color: "text-sky-700" },
    billing: { label: "Billing", icon: MessageCircle, color: "text-amber-700" },
};

export default function SuperSalesLeads() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [openLead, setOpenLead] = useState(null);
    const [unread, setUnread] = useState(0);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/superadmin/sales-leads${filter !== "all" ? `?status=${filter}` : ""}`);
            setRows(data.leads || []);
            setUnread(data.unread_count || 0);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const setStatus = async (id, status) => {
        try {
            await api.post(`/superadmin/sales-leads/${id}/status`, { status });
            toast.success(`Marked as ${status}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    return (
        <>
            <Topbar title="Sales Leads" subtitle={`Contact Sales & Module Requests · ${unread} unread`} />
            <div className="flex-1 overflow-auto p-6" data-testid="super-sales-leads">
                <div className="akm-card p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap gap-1">
                            {["all", "new", "contacted", "in_progress", "won", "lost", "spam"].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setFilter(s)}
                                    data-testid={`filter-${s}`}
                                    className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                                        filter === s
                                            ? "border-[#F26322] bg-[#F26322] text-white"
                                            : "border-slate-200 text-slate-600 hover:border-[#F26322] hover:text-[#F26322]"
                                    }`}
                                >
                                    {s.replace("_", " ")}
                                </button>
                            ))}
                        </div>
                        <button onClick={load} disabled={loading} className="akm-btn-ghost h-8 text-[12px] disabled:opacity-50" data-testid="refresh-leads">
                            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                    </div>

                    {rows.length === 0 ? (
                        <div className="py-12 text-center text-[13px] text-muted-foreground">
                            No sales leads yet. Requests from hotels' "Contact Sales" and "Start a conversation" will appear here.
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {rows.map((r) => {
                                const KM = KIND_META[r.kind] || KIND_META.billing;
                                const KMIcon = KM.icon;
                                return (
                                    <li key={r.id} className="py-4 hover:bg-slate-50/50" data-testid={`lead-row-${r.id}`}>
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpenLead(r)}>
                                                <div className="flex items-center gap-2">
                                                    <KMIcon size={14} className={KM.color} />
                                                    <div className="text-[14px] font-semibold text-slate-900">
                                                        {r.hotel_name || r.hotel_id}
                                                    </div>
                                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                                                        {r.status || "new"}
                                                    </span>
                                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                                        {KM.label}
                                                    </span>
                                                    <span className="text-[11px] text-muted-foreground">
                                                        <Clock size={10} className="inline" /> {formatDateTime(r.created_at)}
                                                    </span>
                                                </div>
                                                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-600">
                                                    <span className="inline-flex items-center gap-1"><Mail size={11} className="text-slate-400" />{r.contact_email || "—"}</span>
                                                    <span className="inline-flex items-center gap-1"><Phone size={11} className="text-slate-400" />{r.contact_phone || "—"}</span>
                                                    <span className="inline-flex items-center gap-1"><Building2 size={11} className="text-slate-400" />by {r.requested_by || "—"}</span>
                                                </div>
                                                {r.message && (
                                                    <div className="mt-1.5 rounded-md bg-slate-50 p-2 text-[12px] text-slate-700 line-clamp-2">
                                                        {r.message}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                <button onClick={() => setOpenLead(r)} data-testid={`lead-open-${r.id}`} className="rounded-lg bg-[#F26322] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#d94d10]">
                                                    <MessageCircle size={11} className="inline -mt-0.5" /> Open
                                                </button>
                                                {["new", "contacted", "in_progress", "won", "lost", "spam"].map((s) => (
                                                    <button
                                                        key={s}
                                                        onClick={() => setStatus(r.id, s)}
                                                        disabled={r.status === s}
                                                        data-testid={`lead-set-${s}-${r.id}`}
                                                        className={`rounded-lg px-2 py-1 text-[10px] font-semibold ${
                                                            r.status === s
                                                                ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                                                : "border border-slate-200 text-slate-600 hover:border-[#F26322] hover:text-[#F26322]"
                                                        }`}
                                                    >
                                                        {s.replace("_", " ")}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                {openLead && <LeadDrawer lead={openLead} onClose={() => setOpenLead(null)} onReload={load} />}
            </div>
        </>
    );
}


function LeadDrawer({ lead, onClose, onReload }) {
    const [detail, setDetail] = useState(null);
    const [reply, setReply] = useState("");
    const [channel, setChannel] = useState("note");
    const [sending, setSending] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/superadmin/sales-leads/${lead.id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [lead.id]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            const { data } = await api.post(`/superadmin/sales-leads/${lead.id}/reply`, {
                message: reply, channel,
            });
            if (channel === "email") {
                toast.success(data.email_status === "sent" ? "Email sent" : "Note saved (email failed)");
            } else {
                toast.success("Note saved");
            }
            setReply("");
            await load();
            await onReload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setSending(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40" onClick={onClose}>
            <div className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
                <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-3">
                    <div>
                        <div className="text-[15px] font-bold">{lead.hotel_name || lead.hotel_id}</div>
                        <div className="text-[12px] text-muted-foreground">Lead #{lead.id.slice(0, 8)} · {lead.kind}</div>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 hover:bg-slate-100" data-testid="lead-drawer-close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5">
                    <div className="mb-4 space-y-1 rounded-lg border p-3 text-[12.5px]">
                        <div><b>Contact:</b> {lead.contact_email || "—"} · {lead.contact_phone || "—"}</div>
                        <div><b>Requested by:</b> {lead.requested_by || "—"}</div>
                        <div><b>Status:</b> <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>{lead.status}</span></div>
                        <div><b>Received:</b> {formatDateTime(lead.created_at)}</div>
                    </div>
                    {lead.message && (
                        <div className="mb-4">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Original Message</div>
                            <div className="rounded-md bg-slate-50 p-3 text-[13px] text-slate-800 whitespace-pre-line">{lead.message}</div>
                        </div>
                    )}
                    <div className="mb-4">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Conversation ({detail?.conversation?.length || 0})</div>
                        {detail?.conversation?.length ? (
                            <ul className="space-y-2">
                                {detail.conversation.map((n) => (
                                    <li key={n.id} className="rounded-md border border-slate-200 p-2.5">
                                        <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted-foreground">
                                            <span className="font-semibold">{n.author_email}</span>
                                            <span>{n.channel} · {formatDateTime(n.created_at)}</span>
                                        </div>
                                        <div className="text-[13px] whitespace-pre-line">{n.message}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="rounded-md bg-slate-50 py-4 text-center text-[12px] text-muted-foreground">No replies yet</div>
                        )}
                    </div>
                    <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add reply</div>
                        <div className="mb-2 flex gap-1">
                            {["note", "email", "phone"].map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setChannel(c)}
                                    data-testid={`lead-channel-${c}`}
                                    className={`rounded-md px-2.5 py-1 text-[10.5px] font-semibold uppercase ${channel === c ? "bg-[#F26322] text-white" : "border border-slate-200 text-slate-600 hover:border-[#F26322]"}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            rows={4}
                            data-testid="lead-reply-textarea"
                            placeholder={channel === "email" ? "Write reply email to hotel..." : channel === "phone" ? "Phone call notes..." : "Internal note..."}
                            className="w-full rounded-md border border-slate-200 p-2 text-[13px]"
                        />
                        <button
                            onClick={sendReply}
                            disabled={sending || !reply.trim()}
                            data-testid="lead-send-reply"
                            className="mt-2 rounded-lg bg-[#F26322] px-4 py-1.5 text-[12px] font-bold text-white hover:bg-[#d94d10] disabled:opacity-50"
                        >
                            <Send size={12} className="inline -mt-0.5" /> {sending ? "Sending..." : channel === "email" ? "Send Email" : "Save Note"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
